import dayjs from "@/utils/dayjs";
import { MOMENT_FORMAT } from "@/constants";
import { ForecastProps, WeatherForecastData } from "@/types/forecast";
import { getCityClock } from "@/utils/cityTime";

const cacheInit = (fresh?: boolean): RequestInit =>
  fresh
    ? ({ cache: "no-store" } as RequestInit)
    : ({ next: { revalidate: 900 } } as RequestInit);

const weatherFnGeneral = (
  { lon, lat }: ForecastProps,
  init?: RequestInit,
  fresh?: boolean,
) => {
  const p = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    daily:
      "weather_code,uv_index_max,wind_direction_10m_dominant,wind_speed_10m_max,temperature_2m_mean,relative_humidity_2m_mean,precipitation_probability_mean,precipitation_sum,precipitation_hours,sunshine_duration,daylight_duration",
    hourly:
      "temperature_2m,cloud_cover_low,cloud_cover_mid,cloud_cover_high,apparent_temperature,dew_point_2m,relative_humidity_2m,wind_speed_10m,wind_direction_10m,wind_gusts_10m,visibility,uv_index,cloud_cover,is_day,weather_code",
    timezone: "auto",
    past_days: "1",
  });
  return fetch(`https://api.open-meteo.com/v1/forecast?${p.toString()}`, {
    method: "GET",
    ...cacheInit(fresh),
    ...init,
  });
};

const weatherFnAir = (
  { lon, lat }: ForecastProps,
  init?: RequestInit,
  fresh?: boolean,
) => {
  const p = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    hourly:
      "european_aqi,pm2_5,pm10,ozone,nitrogen_dioxide,alder_pollen,birch_pollen,grass_pollen,mugwort_pollen,ragweed_pollen",
    timezone: "auto",
    forecast_days: "7",
    past_days: "1",
  });
  return fetch(
    `https://air-quality-api.open-meteo.com/v1/air-quality?${p.toString()}`,
    { method: "GET", ...cacheInit(fresh), ...init },
  );
};

const avgOf = (values: number[]): number | null => {
  const finite = values.filter((v) => Number.isFinite(v));
  if (!finite.length) return null;
  return finite.reduce((sum, v) => sum + v, 0) / finite.length;
};

const loadForecast = async (
  { lat, lon }: ForecastProps,
  { signal }: { signal?: AbortSignal } = {},
  fresh = false,
): Promise<WeatherForecastData> => {
  const [data, airData] = await Promise.all([
    weatherFnGeneral({ lat, lon }, { signal }, fresh),
    weatherFnAir({ lat, lon }, { signal }, fresh),
  ]);

  if (!data.ok || !airData.ok) {
    console.error("API request failed:", {
      status: data.status,
      airStatus: airData.status,
    });
    throw new Error("Failed to fetch weather data from API");
  }

  const forecastData = await data.json();
  const forecastAirData = await airData.json();

  if (!forecastData?.hourly || !forecastAirData?.hourly) {
    console.error("Invalid data structure received from API:", {
      forecastData,
      forecastAirData,
    });
    throw new Error("Invalid data structure from weather API");
  }

  const weatherForecast: WeatherForecastData = {};
  const rawOffset = Number(forecastData.utc_offset_seconds);
  const utcOffsetSeconds = Number.isFinite(rawOffset) ? rawOffset : undefined;
  const timezone =
    typeof forecastData.timezone === "string"
      ? forecastData.timezone
      : undefined;
  const gridLat = Number(forecastData.latitude);
  const gridLon = Number(forecastData.longitude);
  let hourlyIndex = 0;
  let yesterdayAverages: Partial<
    Record<
      | "temperature"
      | "feelsLike"
      | "windSpeed"
      | "humidity"
      | "cloudCover"
      | "uvIndex"
      | "airQuality",
      number | null
    >
  > | null = null;
  let todayKey: string | null = null;
  let prevDayTempAvg: number | null = null;

  try {
    for (let dayIndex = 0; dayIndex < 8; dayIndex++) {
      if (!forecastData.daily?.time?.[dayIndex]) break;
      const temperature: number[] = [];
      const feelsLike: number[] = [];
      const windSpeed: number[] = [];
      const windDirection: number[] = [];
      const windGusts: number[] = [];
      const humidity: number[] = [];
      const dewPoint: number[] = [];
      const visibility: number[] = [];
      const cloudCover: string[] = [];
      const cloudCoverLow: number[] = [];
      const cloudCoverMid: number[] = [];
      const cloudCoverHigh: number[] = [];
      const uvIndex: number[] = [];
      const airQuality: number[] = [];
      const pm25: number[] = [];
      const pm10: number[] = [];
      const ozone: number[] = [];
      const no2: number[] = [];
      const pollenAlder: number[] = [];
      const pollenBirch: number[] = [];
      const pollenGrass: number[] = [];
      const pollenMugwort: number[] = [];
      const pollenRagweed: number[] = [];
      const isDay: number[] = [];
      const hourlyWeatherCode: number[] = [];

      for (let hour = 0; hour < 24; hour++, hourlyIndex++) {
        temperature.push(forecastData.hourly.temperature_2m[hourlyIndex]);
        feelsLike.push(forecastData.hourly.apparent_temperature[hourlyIndex]);
        windSpeed.push(forecastData.hourly.wind_speed_10m[hourlyIndex]);
        windDirection.push(forecastData.hourly.wind_direction_10m[hourlyIndex]);
        windGusts.push(forecastData.hourly.wind_gusts_10m[hourlyIndex]);
        humidity.push(forecastData.hourly.relative_humidity_2m[hourlyIndex]);
        dewPoint.push(forecastData.hourly.dew_point_2m[hourlyIndex]);
        visibility.push(forecastData.hourly.visibility[hourlyIndex]);
        cloudCover.push(forecastData.hourly.cloud_cover[hourlyIndex]);
        cloudCoverLow.push(forecastData.hourly.cloud_cover_low[hourlyIndex]);
        cloudCoverMid.push(forecastData.hourly.cloud_cover_mid[hourlyIndex]);
        cloudCoverHigh.push(forecastData.hourly.cloud_cover_high[hourlyIndex]);
        uvIndex.push(forecastData.hourly.uv_index[hourlyIndex]);
        airQuality.push(forecastAirData.hourly.european_aqi[hourlyIndex]);
        pm25.push(forecastAirData.hourly.pm2_5?.[hourlyIndex]);
        pm10.push(forecastAirData.hourly.pm10?.[hourlyIndex]);
        ozone.push(forecastAirData.hourly.ozone?.[hourlyIndex]);
        no2.push(forecastAirData.hourly.nitrogen_dioxide?.[hourlyIndex]);
        pollenAlder.push(forecastAirData.hourly.alder_pollen?.[hourlyIndex]);
        pollenBirch.push(forecastAirData.hourly.birch_pollen?.[hourlyIndex]);
        pollenGrass.push(forecastAirData.hourly.grass_pollen?.[hourlyIndex]);
        pollenMugwort.push(
          forecastAirData.hourly.mugwort_pollen?.[hourlyIndex],
        );
        pollenRagweed.push(
          forecastAirData.hourly.ragweed_pollen?.[hourlyIndex],
        );
        isDay.push(forecastData.hourly.is_day[hourlyIndex]);
        hourlyWeatherCode.push(forecastData.hourly.weather_code?.[hourlyIndex]);
      }

      if (dayIndex === 0) {
        yesterdayAverages = {
          temperature: avgOf(temperature),
          feelsLike: avgOf(feelsLike),
          windSpeed: avgOf(windSpeed),
          humidity: avgOf(humidity),
          cloudCover: avgOf(cloudCover.map(Number)),
          uvIndex: avgOf(uvIndex),
          airQuality: avgOf(airQuality),
        };
        prevDayTempAvg = yesterdayAverages.temperature ?? null;
        continue;
      }

      const thisTempAvg = avgOf(temperature);
      const currentDay = dayjs(forecastData.daily.time[dayIndex])
        .locale("uk")
        .format(MOMENT_FORMAT);

      weatherForecast[currentDay] = {
        date: currentDay,
        tempVsPrev:
          prevDayTempAvg != null && thisTempAvg != null
            ? thisTempAvg - prevDayTempAvg
            : undefined,
        temperature,
        feelsLike,
        windSpeed,
        windDirection,
        windGusts,
        humidity,
        dewPoint,
        visibility,
        cloudCover,
        cloudCoverLow,
        cloudCoverMid,
        cloudCoverHigh,
        uvIndex,
        airQuality,
        pm25,
        pm10,
        ozone,
        no2,
        pollen: {
          alder: pollenAlder,
          birch: pollenBirch,
          grass: pollenGrass,
          mugwort: pollenMugwort,
          ragweed: pollenRagweed,
        },
        isDay,
        hourlyWeatherCode,
        utcOffsetSeconds,
        timezone,
        lat: Number.isFinite(gridLat) ? gridLat : lat,
        lon: Number.isFinite(gridLon) ? gridLon : lon,
        weatherCode: forecastData.daily.weather_code[dayIndex],
        precipitation:
          forecastData.daily.precipitation_probability_mean[dayIndex],
        precipitationSum: forecastData.daily.precipitation_sum[dayIndex],
        precipitationHours: forecastData.daily.precipitation_hours[dayIndex],
        maxTemperature: Math.max(...temperature),
        minTemperature: Math.min(...temperature),
        sunshineDuration: forecastData.daily.sunshine_duration?.[dayIndex],
        daylightDuration: forecastData.daily.daylight_duration?.[dayIndex],
      };

      if (dayIndex === 1) todayKey = currentDay;
      prevDayTempAvg = thisTempAvg ?? prevDayTempAvg;
    }

    if (todayKey && yesterdayAverages && weatherForecast[todayKey]) {
      const today = weatherForecast[todayKey];
      const todayAverages: Record<string, number | null> = {
        temperature: avgOf(today.temperature),
        feelsLike: avgOf(today.feelsLike),
        windSpeed: avgOf(today.windSpeed),
        humidity: avgOf(today.humidity),
        cloudCover: avgOf(today.cloudCover.map(Number)),
        uvIndex: avgOf(today.uvIndex),
        airQuality: avgOf(today.airQuality),
      };
      const vsYesterday: Record<string, number> = {};
      Object.entries(todayAverages).forEach(([key, todayAvg]) => {
        const yesterdayAvg = (yesterdayAverages as Record<string, number>)[key];
        if (
          typeof todayAvg === "number" &&
          typeof yesterdayAvg === "number" &&
          Number.isFinite(todayAvg) &&
          Number.isFinite(yesterdayAvg)
        ) {
          vsYesterday[key] = todayAvg - yesterdayAvg;
        }
      });
      if (Object.keys(vsYesterday).length) {
        today.vsYesterday = vsYesterday;
      }
    }
  } catch (e) {
    console.error("forecast error: ", e, " hourlyIndex: ", hourlyIndex);
  }

  const { todayKey: cityTodayKey } = getCityClock(utcOffsetSeconds);
  for (const key of Object.keys(weatherForecast)) {
    if (key < cityTodayKey) delete weatherForecast[key];
  }

  return weatherForecast;
};

const FORECAST_HORIZON_DAYS = 6;

const addDaysToKey = (key: string, days: number): string => {
  const d = new Date(`${key}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

export const fetchForecast = async (
  coords: ForecastProps,
  opts: { signal?: AbortSignal } = {},
): Promise<WeatherForecastData> => {
  const cached = await loadForecast(coords, opts, false);
  const keys = Object.keys(cached).sort();
  const offset = cached[keys[0]]?.utcOffsetSeconds;
  const horizon = addDaysToKey(
    getCityClock(offset).todayKey,
    FORECAST_HORIZON_DAYS,
  );
  if (keys.length && keys[keys.length - 1] >= horizon) return cached;

  try {
    const fresh = await loadForecast(coords, opts, true);
    if (Object.keys(fresh).length >= keys.length) return fresh;
  } catch (e) {
    console.error("forecast self-heal refetch failed:", e);
  }
  return cached;
};

export const fetchDetailedSummary = async ({ lat, lon, city }) => {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    daily:
      "weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_mean,wind_speed_10m_max",
    current: "weather_code,temperature_2m",
    timezone: "auto",
    forecast_days: "1",
  });
  const response = await fetch(
    `https://api.open-meteo.com/v1/forecast?${params.toString()}`,
    { next: { revalidate: 900 } },
  );

  if (!response.ok) throw new Error(`Failed to fetch for ${city}`);
  const forecastData = await response.json();

  return {
    city,
    lon: forecastData.longitude,
    lat: forecastData.latitude,
    weatherCode:
      forecastData.current?.weather_code ?? forecastData.daily.weather_code[0],
    timezone:
      typeof forecastData.timezone === "string"
        ? forecastData.timezone
        : undefined,
    utcOffsetSeconds: Number.isFinite(Number(forecastData.utc_offset_seconds))
      ? Number(forecastData.utc_offset_seconds)
      : undefined,
    tempMin: forecastData.daily.temperature_2m_min[0],
    tempMax: forecastData.daily.temperature_2m_max[0],
    temperature:
      forecastData.current?.temperature_2m ??
      forecastData.daily.temperature_2m_max[0],
    windSpeed: forecastData.daily.wind_speed_10m_max[0],
    precipitation: forecastData.daily.precipitation_probability_mean[0],
  };
};
